import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function CaseStudyPage() {
  const { projectId } = useParams();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Case study data
  const caseStudies: Record<string, any> = {
    'metropolitan-office-tower': {
      title: 'Metropolitan Financial Center',
      category: 'AutoCAD & Revit Integration',
      tagline: 'Precision-driven design for downtown landmark development',
      heroImage: 'https://placehold.co/600x400',
      client: 'Metropolitan Development Group',
      industry: 'Commercial Real Estate',
      projectValue: '$420M',
      timeline: '18 months',
      overview: {
        context: 'Metropolitan Development Group required comprehensive technical documentation for their flagship 50-story mixed-use tower in the downtown financial district. The project combines office spaces, retail, dining, and luxury residential units in a single integrated complex.',
        challenge: 'The primary challenge involved coordinating complex building systems across multiple use types while meeting stringent city planning requirements and ensuring structural integrity for the high-rise construction.',
        solution: 'Our team delivered integrated AutoCAD technical drawings with advanced Revit BIM modeling, enabling seamless coordination between architectural, structural, and MEP systems while providing photorealistic visualizations for stakeholder approval.'
      },
      gallery: [
        {
          title: 'Architectural Floor Plans',
          description: 'Detailed AutoCAD drawings for all 50 floors',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Structural System Details',
          description: 'Complex steel and concrete integration',
          image: 'https://placehold.co/600x400'
        },
        {
          title: '3D BIM Visualization',
          description: 'Complete Revit model with system coordination',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'MEP System Layout',
          description: 'Integrated mechanical electrical plumbing systems',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Exterior Renderings',
          description: 'Photorealistic facade and lighting visualization',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Interior Spaces',
          description: 'Lobby and common area visualizations',
          image: 'https://placehold.co/600x400'
        }
      ],
      challenges: [
        'Complex zoning requirements for mixed-use development',
        'Coordination of 15+ engineering disciplines',
        'Structural challenges for 50-story height',
        'Integration of multiple building systems',
        'Fast-track construction timeline requirements',
        'Historic district aesthetic compliance'
      ],
      solutions: [
        'Advanced BIM coordination preventing 95% of potential conflicts',
        'Integrated project delivery with real-time collaboration',
        'Innovative structural design using hybrid steel-concrete system',
        'Comprehensive clash detection and resolution protocols',
        'Parallel design-construction phasing reducing timeline by 6 months',
        'Contextual design approach respecting historic neighborhood character'
      ],
      outcomes: [
        {
          icon: 'ri-accuracy-line',
          metric: '95%',
          description: 'Reduction in construction conflicts through precise BIM coordination'
        },
        {
          icon: 'ri-time-line',
          metric: '6 months',
          description: 'Timeline acceleration through integrated design-build approach'
        },
        {
          icon: 'ri-money-dollar-circle-line',
          metric: '$12.5M',
          description: 'Cost savings from early conflict detection and resolution'
        },
        {
          icon: 'ri-award-line',
          metric: 'LEED Gold',
          description: 'Sustainability certification achieved through optimized design'
        },
        {
          icon: 'ri-team-line',
          metric: '200+',
          description: 'Technical drawings delivered with zero rework required'
        },
        {
          icon: 'ri-check-double-line',
          metric: '100%',
          description: 'Code compliance achieved on first submission to city planning'
        }
      ],
      testimonial: {
        quote: "The level of precision and coordination DMI delivered was exceptional. Their BIM integration saved us months in construction time and millions in potential conflicts. The project was delivered flawlessly.",
        author: "Michael Harrison",
        position: "Senior Project Manager",
        company: "Metropolitan Development Group"
      }
    },
    'green-valley-medical': {
      title: 'Green Valley Medical Center Expansion',
      category: 'Healthcare Facility Design',
      tagline: 'Precision-driven design for critical healthcare infrastructure',
      heroImage: 'https://placehold.co/600x400',
      client: 'Green Valley Healthcare System',
      industry: 'Healthcare',
      projectValue: '$180M',
      timeline: '24 months',
      overview: {
        context: 'Green Valley Healthcare System needed to expand their main medical center to accommodate growing patient volumes and integrate advanced medical technologies. The expansion included surgical suites, imaging centers, and specialized treatment facilities.',
        challenge: 'The project required complex coordination of specialized medical equipment, stringent infection control requirements, and integration with existing facility operations without disrupting patient care.',
        solution: 'We provided comprehensive Revit BIM modeling with specialized healthcare planning, integrating medical equipment coordination, infection control zoning, and phased construction to maintain continuous hospital operations.'
      },
      gallery: [
        {
          title: 'Surgical Suite Layout',
          description: 'Specialized operating room configurations',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Imaging Center Design',
          description: 'MRI and CT scan facility planning',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Patient Flow Analysis',
          description: 'Optimized circulation and wayfinding',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'HVAC System Design',
          description: 'Specialized medical air handling systems',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Emergency Department',
          description: 'Trauma bay and emergency care layout',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Interior Renderings',
          description: 'Healing environment visualization',
          image: 'https://placehold.co/600x400'
        }
      ],
      challenges: [
        'Stringent healthcare regulatory compliance requirements',
        'Integration of specialized medical equipment',
        'Infection control and air quality management',
        'Phased construction without disrupting patient care',
        'Complex MEP systems for medical applications',
        'Seismic and emergency resilience requirements'
      ],
      solutions: [
        'Comprehensive regulatory review and compliance verification',
        'Advanced BIM coordination with medical equipment manufacturers',
        'Specialized HVAC design with positive pressure zones',
        'Detailed construction phasing with temporary care facilities',
        'Redundant MEP systems ensuring continuous operation',
        'Enhanced structural design exceeding seismic code requirements'
      ],
      outcomes: [
        {
          icon: 'ri-heart-pulse-line',
          metric: '0',
          description: 'Patient care disruptions during 24-month construction'
        },
        {
          icon: 'ri-shield-check-line',
          metric: '100%',
          description: 'Regulatory compliance achieved on first inspection'
        },
        {
          icon: 'ri-user-heart-line',
          metric: '40%',
          description: 'Increase in patient capacity with new expansion'
        },
        {
          icon: 'ri-funds-line',
          metric: '$2.3M',
          description: 'Cost savings through advanced clash detection'
        },
        {
          icon: 'ri-time-line',
          metric: '3 months',
          description: 'Early completion ahead of scheduled timeline'
        },
        {
          icon: 'ri-medal-line',
          metric: 'Top 10',
          description: 'Healthcare facility design award recognition'
        }
      ],
      testimonial: {
        quote: "DMI understood the complexity of healthcare design from day one. Their attention to regulatory details and patient care continuity was remarkable. The facility exceeded all our expectations.",
        author: "Dr. Sarah Chen",
        position: "Chief Medical Officer",
        company: "Green Valley Healthcare System"
      }
    },
    'smart-manufacturing-hub': {
      title: 'Advanced Manufacturing Automation Hub',
      category: 'Industrial Systems Integration',
      tagline: 'Precision-driven design for next-generation manufacturing excellence',
      heroImage: 'https://placehold.co/600x400',
      client: 'TechFlow Industries',
      industry: 'Advanced Manufacturing',
      projectValue: '$95M',
      timeline: '15 months',
      overview: {
        context: 'TechFlow Industries required a state-of-the-art manufacturing facility incorporating Industry 4.0 technologies, robotic automation, and sustainable production processes for their next-generation product line.',
        challenge: 'The project demanded integration of complex automation systems, precise environmental controls, and flexible manufacturing layouts while meeting stringent quality and safety standards.',
        solution: 'Our team delivered comprehensive line diagrams, automation schematics, and facility layouts using advanced technical documentation, enabling seamless integration of robotic systems and smart manufacturing technologies.'
      },
      gallery: [
        {
          title: 'Automation Control Systems',
          description: 'Complete robotic integration schematics',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Production Line Layout',
          description: 'Optimized workflow and equipment positioning',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Electrical Distribution',
          description: 'Power systems for automated equipment',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Quality Control Stations',
          description: 'Integrated inspection and testing areas',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Environmental Systems',
          description: 'Climate control and air filtration design',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Safety Systems Integration',
          description: 'Emergency shutdown and safety protocols',
          image: 'https://placehold.co/600x400'
        }
      ],
      challenges: [
        'Integration of multiple automation vendors and systems',
        'Precise environmental controls for quality manufacturing',
        'Complex electrical and control system coordination',
        'Flexible layout accommodating future technology upgrades',
        'Stringent safety and emergency response requirements',
        'Sustainability and energy efficiency optimization'
      ],
      solutions: [
        'Universal communication protocols enabling seamless vendor integration',
        'Advanced HVAC design with micro-climate control zones',
        'Comprehensive electrical load analysis with redundant power systems',
        'Modular facility design allowing for rapid reconfiguration',
        'Multi-layered safety systems with automated emergency protocols',
        'Energy recovery systems reducing operational costs by 35%'
      ],
      outcomes: [
        {
          icon: 'ri-robot-line',
          metric: '99.8%',
          description: 'Manufacturing automation uptime achieved through precise integration'
        },
        {
          icon: 'ri-speed-up-line',
          metric: '45%',
          description: 'Production efficiency increase over previous facility'
        },
        {
          icon: 'ri-leaf-line',
          metric: '35%',
          description: 'Energy consumption reduction through optimized systems'
        },
        {
          icon: 'ri-shield-check-line',
          metric: '0',
          description: 'Safety incidents in first year of operation'
        },
        {
          icon: 'ri-tools-line',
          metric: '2 hours',
          description: 'Production line reconfiguration time for new products'
        },
        {
          icon: 'ri-award-line',
          metric: 'Industry 4.0',
          description: 'Excellence award for smart manufacturing integration'
        }
      ],
      testimonial: {
        quote: "The technical precision DMI brought to our automation project was outstanding. Every system integrated flawlessly, and we achieved production targets 3 months ahead of schedule.",
        author: "James Rodriguez",
        position: "Director of Operations",
        company: "TechFlow Industries"
      }
    }
  };

  // Get current case study or default to first one
  const currentProject = caseStudies[projectId || 'metropolitan-office-tower'] || caseStudies['metropolitan-office-tower'];



  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 md:h-[500px] overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url('${currentProject.heroImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 via-[#212026]/40 to-[#212026]/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-16">
            <div className="max-w-4xl">
              <div className="inline-block px-4 py-2 bg-[#e18033] text-white text-sm font-medium rounded-full mb-6">
                {currentProject.category}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {currentProject.title}
              </h1>
              <p className="text-xl md:text-2xl text-[#dbcfbf] mb-8">
                {currentProject.tagline}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-[#dbcfbf]/80 mb-1">Client</div>
                  <div className="text-white font-semibold">{currentProject.client}</div>
                </div>
                <div>
                  <div className="text-sm text-[#dbcfbf]/80 mb-1">Industry</div>
                  <div className="text-white font-semibold">{currentProject.industry}</div>
                </div>
                <div>
                  <div className="text-sm text-[#dbcfbf]/80 mb-1">Value</div>
                  <div className="text-white font-semibold">{currentProject.projectValue}</div>
                </div>
                <div>
                  <div className="text-sm text-[#dbcfbf]/80 mb-1">Timeline</div>
                  <div className="text-white font-semibold">{currentProject.timeline}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Project Overview */}
        <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">Project Context</h2>
                <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  {currentProject.overview.context}
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">Design Challenge</h2>
                <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  {currentProject.overview.challenge}
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">Our Solution</h2>
                <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  {currentProject.overview.solution}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Visual Gallery */}
        <section className="py-20 bg-[#dbcfbf]/20 dark:bg-slate-800/50 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                Project Gallery
              </h2>
              <p className="text-xl text-[#889d8f] dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                Visual documentation of design process and final deliverables
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentProject.gallery.map((item: any, index: number) => (
                <Card
                  key={index}
                  className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300"
                  onClick={() => setSelectedImage(selectedImage === index ? null : index)}
                >
                  <div className="relative h-64 bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="text-center text-white">
                        <div className="w-12 h-12 bg-[#e18033] rounded-full flex items-center justify-center mx-auto mb-2">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-eye-line text-xl"></i>
                          </div>
                        </div>
                        <p className="text-sm font-medium">View Full Size</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#212026] dark:text-gray-100 mb-2 group-hover:text-[#e18033] transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-[#889d8f] dark:text-gray-300 text-sm leading-relaxed transition-colors duration-300">
                      {item.description}
                    </p>
                  </div>

                  {/* Lightbox Modal */}
                  {selectedImage === index && (
                    <div className="fixed inset-0 bg-[#212026]/90 dark:bg-black/95 flex items-center justify-center z-50 p-4 transition-colors duration-300">
                      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-auto transition-colors duration-300">
                        <div className="relative">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-64 md:h-[500px] object-cover object-top"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImage(null);
                            }}
                            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-[#e18033]/30 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300"
                          >
                            <div className="w-6 h-6 flex items-center justify-center">
                              <i className="ri-close-line text-white text-xl"></i>
                            </div>
                          </button>
                        </div>
                        <div className="p-8">
                          <h3 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                            {item.title}
                          </h3>
                          <p className="text-[#889d8f] dark:text-gray-300 text-lg leading-relaxed transition-colors duration-300">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                Challenges & Solutions
              </h2>
              <p className="text-xl text-[#889d8f] dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                How we overcame complex engineering challenges to deliver exceptional results
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Challenges */}
              <div>
                <h3 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-8 flex items-center transition-colors duration-300">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-error-warning-line text-red-600 dark:text-red-400 text-lg"></i>
                    </div>
                  </div>
                  Project Challenges
                </h3>
                <div className="space-y-4">
                  {currentProject.challenges.map((challenge: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-alert-line text-red-600 dark:text-red-400 text-sm"></i>
                        </div>
                      </div>
                      <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solutions */}
              <div>
                <h3 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-8 flex items-center transition-colors duration-300">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-lightbulb-line text-green-600 dark:text-green-400 text-lg"></i>
                    </div>
                  </div>
                  Our Solutions
                </h3>
                <div className="space-y-4">
                  {currentProject.solutions.map((solution: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-check-line text-green-600 dark:text-green-400 text-sm"></i>
                        </div>
                      </div>
                      <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">{solution}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Outcome / Value Delivered Section */}
        <section className="py-20 bg-[#dbcfbf]/20 dark:bg-slate-800/50 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                Measurable Results
              </h2>
              <p className="text-xl text-[#889d8f] dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                Quantified outcomes demonstrating the value of precision engineering
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentProject.outcomes.map((outcome: any, index: number) => (
                <Card key={index} className="text-center p-8 bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300">
                  <div className="w-16 h-16 bg-[#e18033]/10 dark:bg-[#e18033]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${outcome.icon} text-[#e18033] text-2xl`}></i>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-[#212026] dark:text-gray-100 mb-2 transition-colors duration-300">
                    {outcome.metric}
                  </div>
                  <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                    {outcome.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        {/* <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-12 text-center bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300">
              <div className="w-16 h-16 bg-[#e18033]/10 dark:bg-[#e18033]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-[#e18033] text-2xl"></i>
                </div>
              </div>
              <blockquote className="text-xl md:text-2xl text-[#212026] dark:text-gray-100 leading-relaxed mb-8 italic transition-colors duration-300">
                "{currentProject.testimonial.quote}"
              </blockquote>
              <div className="text-center">
                <div className="text-lg font-semibold text-[#212026] dark:text-gray-100 mb-1 transition-colors duration-300">
                  {currentProject.testimonial.author}
                </div>
                <div className="text-[#e18033] font-medium mb-1">
                  {currentProject.testimonial.position}
                </div>
                <div className="text-[#889d8f] dark:text-gray-300 transition-colors duration-300">
                  {currentProject.testimonial.company}
                </div>
              </div>
            </Card>
          </div>
        </section> */}

        {/* Downloadable Resource Section */}
        {/* <section className="py-20 bg-[#dbcfbf]/20 dark:bg-slate-800/50 transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-6 transition-colors duration-300">
              Get the Complete Story
            </h2>
            <p className="text-xl text-[#889d8f] dark:text-gray-300 mb-8 max-w-2xl mx-auto transition-colors duration-300">
              Download the detailed case study with technical specifications, timelines, and additional project insights
            </p>
            <Card className="p-8 max-w-md mx-auto bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300">
              <div className="w-16 h-16 bg-[#e18033]/10 dark:bg-[#e18033]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-file-pdf-line text-[#e18033] text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#212026] dark:text-gray-100 mb-2 transition-colors duration-300">
                Complete Case Study
              </h3>
              <p className="text-[#889d8f] dark:text-gray-300 mb-6 transition-colors duration-300">
                Detailed technical documentation with project specifications and outcomes
              </p>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#212026] dark:border-[#dbcfbf] dark:hover:bg-[#dbcfbf] dark:hover:text-[#212026] transition-all duration-300">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-download-line"></i>
                </div>
                Download Case Study
              </Button>
            </Card>
          </div>
        </section> */}

        {/* Call-to-Action Section */}
        <section className="py-20 bg-[#e18033] dark:bg-[#e6951f] transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Looking for similar results? Let's work together.
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Ready to bring the same level of precision and expertise to your next project? Our team is here to deliver exceptional results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">

              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-slate-800 dark:border-gray-300 dark:hover:bg-gray-300 dark:hover:text-slate-900 transition-all duration-300">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                Contact Us
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
